import type { AssetType, AssetRenderer } from './types.js';
import * as waterWhale from './renderers/water-whale.js';
import * as waterTurtle from './renderers/water-turtle.js';
import * as shoreWetlandRock from './renderers/shore-wetland-rock.js';
import * as grasslandPine from './renderers/grassland-pine.js';
import * as grasslandRabbit from './renderers/grassland-rabbit.js';
import * as forestWillow from './renderers/forest-willow.js';
import * as farmWheat from './renderers/farm-wheat.js';
import * as farmSheep from './renderers/farm-sheep.js';
import * as farmChicken from './renderers/farm-chicken.js';
import * as farmDonkey from './renderers/farm-donkey.js';
import * as farmPigpen from './renderers/farm-pigpen.js';
import * as farmPearTree from './renderers/farm-pear-tree.js';
import * as villageTent from './renderers/village-tent.js';
import * as villageShrine from './renderers/village-shrine.js';
import * as townCityMarket from './renderers/town-city-market.js';
import * as townCityManor from './renderers/town-city-manor.js';
import * as biomeBlendReeds from './renderers/biome-blend-reeds.js';
import * as crossLevelCart from './renderers/cross-level-cart.js';
import * as seasonalWinterSnowPine from './renderers/seasonal-winter-snow-pine.js';
import * as seasonalWinterIgloo from './renderers/seasonal-winter-igloo.js';
import * as seasonalWinterBareBush from './renderers/seasonal-winter-bare-bush.js';
import * as seasonalWinterChurchWinter from './renderers/seasonal-winter-church-winter.js';
import * as seasonalSpringCherryBlossom from './renderers/seasonal-spring-cherry-blossom.js';
import * as seasonalSpringNest from './renderers/seasonal-spring-nest.js';
import * as seasonalSpringCherryBlossomFull from './renderers/seasonal-spring-cherry-blossom-full.js';
import * as seasonalSpringRobinBird from './renderers/seasonal-spring-robin-bird.js';
import * as seasonalSummerParasol from './renderers/seasonal-summer-parasol.js';
import * as seasonalSummerLemonade from './renderers/seasonal-summer-lemonade.js';
import * as seasonalAutumnAutumnMaple from './renderers/seasonal-autumn-autumn-maple.js';
import * as seasonalAutumnCornStalk from './renderers/seasonal-autumn-corn-stalk.js';
import * as seasonalAutumnAppleBasket from './renderers/seasonal-autumn-apple-basket.js';
import { NATURE_RENDERERS } from './renderers/nature.js';

import { svgHanok, svgPavilion, svgStoneWall, svgOnggi } from './renderers/korean-village.js';
import {
  svgChoga,
  svgKoreanWatermill,
  svgHanokGate,
  svgHanokEstate,
} from './renderers/korean-rural-buildings.js';
import { svgJangseung, svgSotdae, svgStoneBridge } from './renderers/korean-rural-props.js';
import { svgRiceTerrace, svgKimchiGarden } from './renderers/korean-rural-fields.js';

export const ASSET_RENDERERS: Record<AssetType, AssetRenderer> = {
  whale: waterWhale.svgWhale,
  fish: waterWhale.svgFish,
  fishSchool: waterWhale.svgFishSchool,
  boat: waterWhale.svgBoat,
  seagull: waterWhale.svgSeagull,
  dock: waterWhale.svgDock,
  waves: waterWhale.svgWaves,
  kelp: waterWhale.svgKelp,
  coral: waterWhale.svgCoral,
  jellyfish: waterWhale.svgJellyfish,
  turtle: waterTurtle.svgTurtle,
  buoy: waterTurtle.svgBuoy,
  sailboat: waterTurtle.svgSailboat,
  lighthouse: waterTurtle.svgLighthouse,
  crab: waterTurtle.svgCrab,
  rock: shoreWetlandRock.svgRock,
  boulder: shoreWetlandRock.svgBoulder,
  flower: shoreWetlandRock.svgFlower,
  bush: shoreWetlandRock.svgBush,
  driftwood: shoreWetlandRock.svgDriftwood,
  sandcastle: shoreWetlandRock.svgSandcastle,
  tidePools: shoreWetlandRock.svgTidePools,
  heron: shoreWetlandRock.svgHeron,
  shellfish: shoreWetlandRock.svgShellfish,
  cattail: shoreWetlandRock.svgCattail,
  frog: shoreWetlandRock.svgFrog,
  lily: shoreWetlandRock.svgLily,
  pine: grasslandPine.svgPine,
  deciduous: grasslandPine.svgDeciduous,
  mushroom: grasslandPine.svgMushroom,
  stump: grasslandPine.svgStump,
  deer: grasslandPine.svgDeer,
  rabbit: grasslandRabbit.svgRabbit,
  fox: grasslandRabbit.svgFox,
  butterfly: grasslandRabbit.svgButterfly,
  beehive: grasslandRabbit.svgBeehive,
  wildflowerPatch: grasslandRabbit.svgWildflowerPatch,
  tallGrass: grasslandRabbit.svgTallGrass,
  birch: grasslandRabbit.svgBirch,
  haybale: grasslandRabbit.svgHaybale,
  willow: forestWillow.svgWillow,
  palm: forestWillow.svgPalm,
  bird: forestWillow.svgBird,
  owl: forestWillow.svgOwl,
  squirrel: forestWillow.svgSquirrel,
  moss: forestWillow.svgMoss,
  fern: forestWillow.svgFern,
  deadTree: forestWillow.svgDeadTree,
  log: forestWillow.svgLog,
  berryBush: forestWillow.svgBerryBush,
  spider: forestWillow.svgSpider,
  wheat: farmWheat.svgWheat,
  fence: farmWheat.svgFence,
  scarecrow: farmWheat.svgScarecrow,
  barn: farmWheat.svgBarn,
  sheep: farmSheep.svgSheep,
  cow: farmSheep.svgCow,
  chicken: farmChicken.svgChicken,
  horse: farmChicken.svgHorse,
  donkey: farmDonkey.svgDonkey,
  goat: farmDonkey.svgGoat,
  ricePaddy: farmDonkey.svgRicePaddy,
  silo: farmDonkey.svgSilo,
  pigpen: farmPigpen.svgPigpen,
  trough: farmPigpen.svgTrough,
  haystack: farmPigpen.svgHaystack,
  orchard: farmPigpen.svgOrchard,
  appleTree: farmPigpen.svgAppleTree,
  oliveTree: farmPigpen.svgOliveTree,
  lemonTree: farmPigpen.svgLemonTree,
  orangeTree: farmPigpen.svgOrangeTree,
  pearTree: farmPearTree.svgPearTree,
  peachTree: farmPearTree.svgPeachTree,
  beeFarm: farmPearTree.svgBeeFarm,
  pumpkin: farmPearTree.svgPumpkin,
  tent: villageTent.svgTent,
  hut: villageTent.svgHut,
  house: villageTent.svgHouse,
  houseB: villageTent.svgHouseB,
  church: villageTent.svgChurch,
  windmill: villageTent.svgWindmill,
  well: villageTent.svgWell,
  tavern: villageTent.svgTavern,
  bakery: villageTent.svgBakery,
  stable: villageTent.svgStable,
  garden: villageTent.svgGarden,
  laundry: villageTent.svgLaundry,
  doghouse: villageTent.svgDoghouse,
  shrine: villageShrine.svgShrine,
  wagon: villageShrine.svgWagon,
  market: townCityMarket.svgMarket,
  inn: townCityMarket.svgInn,
  blacksmith: townCityMarket.svgBlacksmith,
  castle: townCityMarket.svgCastle,
  tower: townCityMarket.svgTower,
  bridge: townCityMarket.svgBridge,
  cathedral: townCityMarket.svgCathedral,
  library: townCityMarket.svgLibrary,
  clocktower: townCityMarket.svgClocktower,
  statue: townCityMarket.svgStatue,
  park: townCityMarket.svgPark,
  warehouse: townCityMarket.svgWarehouse,
  gatehouse: townCityMarket.svgGatehouse,
  manor: townCityManor.svgManor,
  reeds: biomeBlendReeds.svgReeds,
  fountain: biomeBlendReeds.svgFountain,
  canal: biomeBlendReeds.svgCanal,
  watermill: biomeBlendReeds.svgWatermill,
  gardenTree: biomeBlendReeds.svgGardenTree,
  pondLily: biomeBlendReeds.svgPondLily,
  cart: crossLevelCart.svgCart,
  barrel: crossLevelCart.svgBarrel,
  torch: crossLevelCart.svgTorch,
  flag: crossLevelCart.svgFlag,
  cobblePath: crossLevelCart.svgCobblePath,
  smoke: crossLevelCart.svgSmoke,
  signpost: crossLevelCart.svgSignpost,
  lantern: crossLevelCart.svgLantern,
  woodpile: crossLevelCart.svgWoodpile,
  puddle: crossLevelCart.svgPuddle,
  campfire: crossLevelCart.svgCampfire,
  snowPine: seasonalWinterSnowPine.svgSnowPine,
  snowDeciduous: seasonalWinterSnowPine.svgSnowDeciduous,
  snowman: seasonalWinterSnowPine.svgSnowman,
  snowdrift: seasonalWinterSnowPine.svgSnowdrift,
  igloo: seasonalWinterIgloo.svgIgloo,
  frozenPond: seasonalWinterIgloo.svgFrozenPond,
  icicle: seasonalWinterIgloo.svgIcicle,
  sled: seasonalWinterIgloo.svgSled,
  snowCoveredRock: seasonalWinterIgloo.svgSnowCoveredRock,
  bareBush: seasonalWinterBareBush.svgBareBush,
  winterBird: seasonalWinterBareBush.svgWinterBird,
  firewood: seasonalWinterBareBush.svgFirewood,
  houseWinter: seasonalWinterBareBush.svgHouseWinter,
  houseBWinter: seasonalWinterBareBush.svgHouseBWinter,
  barnWinter: seasonalWinterBareBush.svgBarnWinter,
  churchWinter: seasonalWinterChurchWinter.svgChurchWinter,
  christmasTree: seasonalWinterChurchWinter.svgChristmasTree,
  winterLantern: seasonalWinterChurchWinter.svgWinterLantern,
  frozenFountain: seasonalWinterChurchWinter.svgFrozenFountain,
  cherryBlossom: seasonalSpringCherryBlossom.svgCherryBlossom,
  cherryBlossomSmall: seasonalSpringCherryBlossom.svgCherryBlossomSmall,
  cherryPetals: seasonalSpringCherryBlossom.svgCherryPetals,
  tulip: seasonalSpringCherryBlossom.svgTulip,
  tulipField: seasonalSpringCherryBlossom.svgTulipField,
  sprout: seasonalSpringCherryBlossom.svgSprout,
  nest: seasonalSpringNest.svgNest,
  lamb: seasonalSpringNest.svgLamb,
  crocus: seasonalSpringNest.svgCrocus,
  rainPuddle: seasonalSpringNest.svgRainPuddle,
  birdhouse: seasonalSpringNest.svgBirdhouse,
  gardenBed: seasonalSpringNest.svgGardenBed,
  cherryBlossomFull: seasonalSpringCherryBlossomFull.svgCherryBlossomFull,
  cherryBlossomBranch: seasonalSpringCherryBlossomFull.svgCherryBlossomBranch,
  peachBlossom: seasonalSpringCherryBlossomFull.svgPeachBlossom,
  flowerBed: seasonalSpringCherryBlossomFull.svgFlowerBed,
  wateringCan: seasonalSpringCherryBlossomFull.svgWateringCan,
  seedling: seasonalSpringCherryBlossomFull.svgSeedling,
  robinBird: seasonalSpringRobinBird.svgRobinBird,
  butterflyGarden: seasonalSpringRobinBird.svgButterflyGarden,
  umbrella: seasonalSpringRobinBird.svgUmbrella,
  parasol: seasonalSummerParasol.svgParasol,
  beachTowel: seasonalSummerParasol.svgBeachTowel,
  sandcastleSummer: seasonalSummerParasol.svgSandcastleSummer,
  surfboard: seasonalSummerParasol.svgSurfboard,
  iceCreamCart: seasonalSummerParasol.svgIceCreamCartAsset,
  hammock: seasonalSummerParasol.svgHammock,
  sunflower: seasonalSummerParasol.svgSunflower,
  watermelon: seasonalSummerParasol.svgWatermelon,
  sprinkler: seasonalSummerParasol.svgSprinkler,
  lemonade: seasonalSummerLemonade.svgLemonade,
  fireflies: seasonalSummerLemonade.svgFirefliesAsset,
  swimmingPool: seasonalSummerLemonade.svgSwimmingPool,
  autumnMaple: seasonalAutumnAutumnMaple.svgAutumnMaple,
  autumnOak: seasonalAutumnAutumnMaple.svgAutumnOak,
  autumnBirch: seasonalAutumnAutumnMaple.svgAutumnBirch,
  autumnGinkgo: seasonalAutumnAutumnMaple.svgAutumnGinkgo,
  fallenLeaves: seasonalAutumnAutumnMaple.svgFallenLeaves,
  leafSwirl: seasonalAutumnAutumnMaple.svgLeafSwirl,
  acorn: seasonalAutumnAutumnMaple.svgAcorn,
  cornStalk: seasonalAutumnCornStalk.svgCornStalkAsset,
  scarecrowAutumn: seasonalAutumnCornStalk.svgScarecrowAutumn,
  harvestBasket: seasonalAutumnCornStalk.svgHarvestBasket,
  hotDrink: seasonalAutumnCornStalk.svgHotDrink,
  autumnWreath: seasonalAutumnCornStalk.svgAutumnWreath,
  pumpkinPatch: seasonalAutumnCornStalk.svgPumpkinPatch,
  hayMaze: seasonalAutumnCornStalk.svgHayMaze,
  appleBasket: seasonalAutumnAppleBasket.svgAppleBasket,
  rake: seasonalAutumnAppleBasket.svgRake,
  hanok: svgHanok,
  pavilion: svgPavilion,
  stoneWall: svgStoneWall,
  onggi: svgOnggi,
  choga: svgChoga,
  jangseung: svgJangseung,
  sotdae: svgSotdae,
  riceTerrace: svgRiceTerrace,
  koreanWatermill: svgKoreanWatermill,
  hanokGate: svgHanokGate,
  kimchiGarden: svgKimchiGarden,
  stoneBridge: svgStoneBridge,
  hanokEstate: svgHanokEstate,
  ...NATURE_RENDERERS,
};
